// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { MoveImportDialog } from '@/asset/components/move-import-dialog';
import { FilterMenu } from '@/components/filter-menu';
import { FilterToggleGroup } from '@/components/filter-toggle-group';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { TableControlContainer } from '@/components/table-control-container';
import {
  AddRounded,
  ClearRounded,
  DeleteOutlined,
} from '@/constants/hardfin-standard-icons';
import { SEARCH_TERM_MAXIMUM_CHARACTERS } from '@/constants/search-term';
import { useDisclosure } from '@/hooks/use-disclosure';
import { ItemFilterMenu } from '@/items/components/item-filter-menu';
import { LocationFilterMenu } from '@/location/components/location-filter-menu';
import { Movement, MovementStatus } from '@/movement/api/types';
import { DeleteMovementsDialog } from '@/movement/components/delete-movements-dialog';
import { MovementTable } from '@/movement/components/movement-table';
import { MovementTableSkeleton } from '@/movement/components/movement-table-skeleton';
import { MOVEMENT_STATUS_OPTIONS } from '@/movement/constants/status-filter-options';
import { MovementSearchListing } from '@/movement/hooks/use-movements';
import {
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { debounce } from 'lodash-es';
import {
  ChangeEventHandler,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface NavigateOptions {
  search?: Partial<MovementSearchListing & { status: MovementStatus }>;
}

interface MovementListingProps {
  currentStatus: MovementStatus;
  searchParameters: MovementSearchListing;
  navigateFn: (options: NavigateOptions) => void;
}

export function MovementListing(props: MovementListingProps) {
  const searchContainer = useRef<HTMLInputElement>(null);
  const navigateFn = props.navigateFn;
  const currentStatus = props.currentStatus;
  const searchParameters = props.searchParameters;

  // Bulk mode state
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [selectedMovements, setSelectedMovements] = useState<
    Map<string, Movement>
  >(new Map());
  const deleteDialog = useDisclosure();
  const moveImportDialog = useDisclosure();

  const selectedIds = useMemo(
    () => new Set(selectedMovements.keys()),
    [selectedMovements]
  );

  const selectedMovementsList = useMemo(
    () => Array.from(selectedMovements.values()),
    [selectedMovements]
  );

  const handleEnterBulkMode = () => {
    setIsBulkMode(true);
  };

  const handleExitBulkMode = () => {
    setIsBulkMode(false);
    setSelectedMovements(new Map());
  };

  const handleToggleSelect = useCallback((movement: Movement) => {
    setSelectedMovements((prev) => {
      const next = new Map(prev);
      if (next.has(movement.id)) {
        next.delete(movement.id);
      } else {
        next.set(movement.id, movement);
      }
      return next;
    });
  }, []);

  const handleToggleSelectAll = useCallback((movements: Movement[]) => {
    const selectable = movements.filter((m) => m.shipmentNumber === null);
    setSelectedMovements((prev) => {
      const allSelected = selectable.every((m) => prev.has(m.id));
      if (allSelected) {
        // Deselect all visible selectable
        const next = new Map(prev);
        for (const m of selectable) {
          next.delete(m.id);
        }
        return next;
      }
      // Select all visible selectable
      const next = new Map(prev);
      for (const m of selectable) {
        next.set(m.id, m);
      }
      return next;
    });
  }, []);

  const handleDeleteSuccess = () => {
    setSelectedMovements(new Map());
    setIsBulkMode(false);
  };

  const searchParametersRef = useRef(searchParameters);
  searchParametersRef.current = searchParameters;

  const updateQuery = useMemo(() => {
    return debounce((query: string) => {
      navigateFn({
        search: {
          ...searchParametersRef.current,
          status: currentStatus,
          page: undefined,
          query: query.length > 0 ? query : undefined,
        },
      });
    }, 300);
  }, [navigateFn, currentStatus]);

  useEffect(() => {
    return () => {
      updateQuery.cancel();
    };
  }, [updateQuery]);

  const handleStatusChange = (newStatus: MovementStatus) => {
    props.navigateFn({
      search: {
        ...searchParameters,
        status: newStatus,
        page: undefined,
      },
    });
  };

  const onPageChange = (pageNumber: number) => {
    props.navigateFn({
      search: {
        ...searchParameters,
        status: currentStatus,
        page: pageNumber,
      },
    });
  };

  const handleChangeQuery: ChangeEventHandler<HTMLInputElement> = (event) => {
    updateQuery(event.target.value);
  };

  const handleClearSearch = () => {
    if (searchContainer.current) {
      searchContainer.current.value = '';
    }
    props.navigateFn({
      search: {
        ...searchParameters,
        status: currentStatus,
        page: undefined,
        query: undefined,
      },
    });
  };

  const handleChangeProducts = (itemIds: string[]) => {
    props.navigateFn({
      search: {
        ...searchParameters,
        status: currentStatus,
        page: undefined,
        itemIds: itemIds.length > 0 ? itemIds : undefined,
      },
    });
  };

  const handleChangeOrigins = (originIds: string[]) => {
    props.navigateFn({
      search: {
        ...searchParameters,
        status: currentStatus,
        page: undefined,
        originIds: originIds.length > 0 ? originIds : undefined,
      },
    });
  };

  const handleChangeDestinations = (destinationIds: string[]) => {
    props.navigateFn({
      search: {
        ...searchParameters,
        status: currentStatus,
        page: undefined,
        destinationIds: destinationIds.length > 0 ? destinationIds : undefined,
      },
    });
  };

  const shipmentFilterValues = searchParameters.hasShipment ?? [];

  const handleChangeShipmentOption = (id: 'yes' | 'no', isActive: boolean) => {
    const next = isActive
      ? [...shipmentFilterValues, id]
      : shipmentFilterValues.filter((v) => v !== id);
    props.navigateFn({
      search: {
        ...searchParameters,
        status: currentStatus,
        page: undefined,
        hasShipment: next.length > 0 ? (next as ('yes' | 'no')[]) : undefined,
      },
    });
  };

  const hasActiveFilters =
    (searchParameters.itemIds && searchParameters.itemIds.length > 0) ||
    (searchParameters.originIds && searchParameters.originIds.length > 0) ||
    (searchParameters.destinationIds &&
      searchParameters.destinationIds.length > 0) ||
    searchParameters.hasShipment !== undefined;

  const handleClearAllFilters = () => {
    props.navigateFn({
      search: {
        status: currentStatus,
        query: searchParameters.query,
        page: undefined,
        itemIds: undefined,
        originIds: undefined,
        destinationIds: undefined,
        hasShipment: undefined,
      },
    });
  };

  return (
    <>
      <DeleteMovementsDialog
        isOpen={deleteDialog.isOpen}
        movements={selectedMovementsList}
        onClose={deleteDialog.close}
        onSuccess={handleDeleteSuccess}
      />
      <MoveImportDialog
        isOpen={moveImportDialog.isOpen}
        onClose={moveImportDialog.close}
      />

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <FilterToggleGroup
          value={currentStatus}
          options={MOVEMENT_STATUS_OPTIONS}
          onChange={handleStatusChange}
          ariaLabel="Movement status filter"
        />
        <Stack direction="row" spacing={1}>
          {isBulkMode ? (
            <>
              <Tooltip
                arrow
                title={
                  selectedMovements.size === 0
                    ? 'Select at least one movement'
                    : ''
                }
              >
                <span>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<DeleteOutlined fontSize="small" />}
                    disabled={selectedMovements.size === 0}
                    onClick={deleteDialog.open}
                  >
                    Review for deletion
                  </Button>
                </span>
              </Tooltip>
              <Button variant="text" onClick={handleExitBulkMode}>
                Cancel editing
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                startIcon={<AddRounded />}
                onClick={moveImportDialog.open}
              >
                Add moves
              </Button>
              <Button variant="contained" onClick={handleEnterBulkMode}>
                Bulk edit
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      <TableControlContainer>
        <Stack
          useFlexGap
          spacing={1}
          flexWrap="wrap"
          direction="row"
          marginRight="auto"
        >
          <ItemFilterMenu
            label="Product"
            value={searchParameters.itemIds ?? []}
            type="DEVICE"
            includeArchived={true}
            onChange={handleChangeProducts}
          />
          <LocationFilterMenu
            label="Origin"
            value={searchParameters.originIds ?? []}
            onChange={handleChangeOrigins}
          />
          <LocationFilterMenu
            label="Destination"
            value={searchParameters.destinationIds ?? []}
            onChange={handleChangeDestinations}
          />
          <FilterMenu
            label="Part of shipment"
            options={[
              {
                id: 'yes' as const,
                label: 'Yes',
                isActive: shipmentFilterValues.includes('yes'),
              },
              {
                id: 'no' as const,
                label: 'No',
                isActive: shipmentFilterValues.includes('no'),
              },
            ]}
            onChangeOption={handleChangeShipmentOption}
          />
          {hasActiveFilters && (
            <Button
              onClick={handleClearAllFilters}
              variant="text"
              color="primary"
              size="small"
              sx={{ marginLeft: 2 }}
            >
              Clear filters
            </Button>
          )}
        </Stack>
        <TextField
          inputRef={searchContainer}
          size="small"
          label="Search by serial"
          defaultValue={searchParameters.query}
          inputProps={{ maxLength: SEARCH_TERM_MAXIMUM_CHARACTERS }}
          onChange={handleChangeQuery}
          sx={{ width: { xs: '100%', sm: '20rem' } }}
          InputProps={{
            endAdornment: searchParameters.query ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearRounded fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </TableControlContainer>

      <SupportErrorBoundary message="An error occurred while loading movements">
        <Suspense fallback={<MovementTableSkeleton />}>
          <MovementTable
            onPageChange={onPageChange}
            isBulkMode={isBulkMode}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            onToggleSelectAll={handleToggleSelectAll}
            filter={{
              status: currentStatus === 'all' ? undefined : currentStatus,
              query: searchParameters.query,
              itemIds: searchParameters.itemIds,
              originIds: searchParameters.originIds,
              destinationIds: searchParameters.destinationIds,
              hasShipment: searchParameters.hasShipment,
              pageNumber: searchParameters.page,
              pageSize: 50,
            }}
          />
        </Suspense>
      </SupportErrorBoundary>
    </>
  );
}
